/* eslint-disable no-console */
import { type PostConsentPayload } from '@carvana/core-consent';
import { type Dispatch } from 'redux';
import get from 'lodash.get';
import * as actions from './actions';
import SuccessToast from '../../../shared/components/Toasts/SuccessToast';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../constants';
import ErrorToast from '../../../shared/components/Toasts/ErrorToast';
import { KissMetrics } from '../../../shared/services';
import ConsentApi from '../../../shared/apis/ConsentApi';
import type { NormalizedConsent, ConsentUpdateRequest } from '../../../shared/apis/ConsentApi';
import type {
  EmailNotificationSettings,
  NotificationSettingsFeatureFlags
} from '../../../contracts';
import {
  CONSENT_TYPE_NAME,
  CONSENT_CHANNEL_STRING,
  CONSENT_SUBTYPE_NAME,
  CONSENT_SOURCE
} from '../constants/consents';

const transformConsentsToNotifications = (consents: NormalizedConsent[]) => {
  const content = consents || [];

  const findConsent = (typeName: string) => content.find((c) => c.typeName === typeName);

  const isOptedIn = (typeName: string) => {
    const consent = findConsent(typeName);
    return consent ? consent.isOptedIn : false;
  };

  const tcpaConsent = findConsent(CONSENT_TYPE_NAME.TCPA);

  return {
    content: {
      general: {
        tcpa: {
          isOptedIn: tcpaConsent ? tcpaConsent.isOptedIn : false,
          isExplicitConsent: tcpaConsent ? tcpaConsent.isExplicitConsent : false
        }
      },
      email: {
        emailAlerts: isOptedIn(CONSENT_TYPE_NAME.EMAIL_ALERTS),
        emailEvents: isOptedIn(CONSENT_TYPE_NAME.EMAIL_EVENTS),
        emailOffers: isOptedIn(CONSENT_TYPE_NAME.EMAIL_OFFERS),
        emailPromotions: isOptedIn(CONSENT_TYPE_NAME.EMAIL_PROMOTIONS),
        emailGlobal: isOptedIn(CONSENT_TYPE_NAME.EMAIL_GLOBAL)
      }
    }
  };
};

export const fetchAllConsents = (userId: string) => (dispatch: Dispatch) => {
  dispatch(actions.fetchNotificationsBegin());
  dispatch(actions.fetchSoftpullConsentBegin());

  return ConsentApi.getConsents(userId)
    .then((response) => {
      const consents = response.data.content;

      const notifications = transformConsentsToNotifications(consents);
      dispatch(actions.fetchNotificationsSuccess(notifications));

      const softpullConsent = consents.find(
        (consent: NormalizedConsent) =>
          consent.channel === CONSENT_CHANNEL_STRING.GENERAL &&
          consent.typeName === CONSENT_TYPE_NAME.SOFTPULL
      );
      dispatch(actions.fetchSoftpullConsentSuccess(softpullConsent?.isOptedIn ?? false));
    })
    .catch((error) => {
      console.error(error);
      dispatch(actions.fetchNotificationsFailure(error));
      dispatch(actions.fetchSoftpullConsentFailure(error));

      const isInternalServerError = get(error, 'response.status') === 500;
      const errorToastMessage = isInternalServerError
        ? ERROR_MESSAGES.GET_NOTIFICATIONS
        : ERROR_MESSAGES.GET_NOTIFICATIONS_TIMEOUT;
      ErrorToast(errorToastMessage);
    });
};

const getEmailConsentRequest = (
  emailNotifications: EmailNotificationSettings
): ConsentUpdateRequest[] => {
  return [
    {
      consentType: CONSENT_TYPE_NAME.EMAIL_ALERTS,
      isOptedIn: emailNotifications.isSubscribedToAlerts ?? false,
      source: CONSENT_SOURCE
    },
    {
      consentType: CONSENT_TYPE_NAME.EMAIL_EVENTS,
      isOptedIn: emailNotifications.isSubscribedToEvents ?? false,
      source: CONSENT_SOURCE
    },
    {
      consentType: CONSENT_TYPE_NAME.EMAIL_OFFERS,
      isOptedIn: emailNotifications.isSubscribedToOffers ?? false,
      source: CONSENT_SOURCE
    },
    {
      consentType: CONSENT_TYPE_NAME.EMAIL_PROMOTIONS,
      isOptedIn: emailNotifications.isSubscribedToPromotions ?? false,
      source: CONSENT_SOURCE
    },
    {
      consentType: CONSENT_TYPE_NAME.EMAIL_GLOBAL,
      isOptedIn: emailNotifications.isSubscribedGlobal ?? false,
      source: CONSENT_SOURCE
    }
  ];
};

export const getTCPAConsentRequest = (isOptedIn: boolean): ConsentUpdateRequest[] => {
  return [
    {
      consentType: CONSENT_TYPE_NAME.TCPA,
      isOptedIn,
      source: CONSENT_SOURCE
    }
  ];
};

export const getCreditMonitoringConsentRequest = (isOptedIn: boolean): ConsentUpdateRequest[] => {
  return [
    {
      consentType: CONSENT_TYPE_NAME.SOFTPULL,
      consentSubType: CONSENT_SUBTYPE_NAME.CREDIT_MONITORING,
      isOptedIn,
      source: CONSENT_SOURCE
    }
  ];
};

export const updateEmailNotifications =
  (
    emailNotifications: EmailNotificationSettings,
    successMessage: string,
    emailOriginalState: EmailNotificationSettings
  ) =>
  (dispatch: Dispatch) => {
    dispatch(actions.updateEmailNotificationsBegin());
    const isSubscribedToAnyEmail =
      emailNotifications.isSubscribedToAlerts ||
      emailNotifications.isSubscribedToEvents ||
      emailNotifications.isSubscribedToOffers ||
      emailNotifications.isSubscribedToPromotions;
    emailNotifications.isSubscribedGlobal = isSubscribedToAnyEmail;

    const request = getEmailConsentRequest(emailNotifications);

    dispatch(actions.updateEmailNotificationsSuccess(emailNotifications));
    return ConsentApi.updateConsents(request)
      .then(() => {
        KissMetrics.trackEvent('Account - Updated Email Notifications', {
          IsSubscribedToEventsAndSurveys: emailNotifications.isSubscribedToEvents,
          IsSubscribedToPromotions: emailNotifications.isSubscribedToPromotions,
          IsSubscribedToOffers: emailNotifications.isSubscribedToOffers,
          IsSubscribedToCarvanaInventory: emailNotifications.isSubscribedToAlerts,
          WasSuccessful: true
        });
        SuccessToast(successMessage);
      })
      .catch((error) => {
        console.error(error);
        dispatch(actions.updateEmailNotificationsFailure({ error, emailOriginalState }));

        KissMetrics.trackEvent('Account - Updated Email Notifications', {
          IsSubscribedToEventsAndSurveys: emailNotifications.isSubscribedToEvents,
          IsSubscribedToPromotions: emailNotifications.isSubscribedToPromotions,
          IsSubscribedToOffers: emailNotifications.isSubscribedToOffers,
          IsSubscribedToCarvanaInventory: emailNotifications.isSubscribedToAlerts,
          WasSuccessful: false
        });

        const isInternalServerError = get(error, 'response.status') === 500;
        const errorToastMessage = isInternalServerError
          ? ERROR_MESSAGES.UPDATE_EMAIL
          : ERROR_MESSAGES.UPDATE_EMAIL_TIMEOUT;
        ErrorToast(errorToastMessage);
      });
  };

export const updateTCPANotifications =
  (
    isSubscribedToNotificationChannel: boolean,
    notificationSettingsFeatureFlags: NotificationSettingsFeatureFlags
  ) =>
  (dispatch: Dispatch) => {
    const { shouldShowSms, shouldShowPhoneCall } = notificationSettingsFeatureFlags;
    if (shouldShowSms || shouldShowPhoneCall) dispatch(actions.updateTCPANotificationsBegin());

    const request = getTCPAConsentRequest(isSubscribedToNotificationChannel);

    dispatch(actions.updateTCPANotificationsSuccess(isSubscribedToNotificationChannel));

    ConsentApi.updateConsents(request)
      .then(() => {
        const toastMessage = isSubscribedToNotificationChannel
          ? SUCCESS_MESSAGES.PHONE_AND_SMS_SUBSCRIBE
          : SUCCESS_MESSAGES.PHONE_AND_SMS_UNSUBSCRIBE;
        SuccessToast(toastMessage);

        KissMetrics.trackEvent('Account - Updated Phone Notifications', {
          IsSubscribedToTCPA: isSubscribedToNotificationChannel,
          WasSuccessful: true,
          FeatureFlags: notificationSettingsFeatureFlags
        });
      })
      .catch((error) => {
        ErrorToast(ERROR_MESSAGES.UPDATE_PHONE_AND_SMS);
        dispatch(actions.updateTCPANotificationsSuccess(!isSubscribedToNotificationChannel));
        dispatch(actions.updateTCPANotificationsFailure(error));

        KissMetrics.trackEvent('Account - Updated Phone Notifications', {
          IsSubscribedToTCPA: isSubscribedToNotificationChannel,
          WasSuccessful: false,
          FeatureFlags: notificationSettingsFeatureFlags
        });

        console.error(error);
      });
  };

export const updateCreditMonitoringConsent =
  (
    postConsent: (request: PostConsentPayload[]) => Promise<unknown>,
    creditMonitoringConsent: boolean
  ) =>
  (dispatch: Dispatch) => {
    const request = getCreditMonitoringConsentRequest(creditMonitoringConsent);

    dispatch(actions.updateCreditMonitoringConsentSuccess(creditMonitoringConsent));

    postConsent(request as PostConsentPayload[]) // TODO: Remove this cast after updating core-consent package to support the types that we need to send
      .then(() => {
        const toastMessage = creditMonitoringConsent
          ? SUCCESS_MESSAGES.CREDIT_MONITORING_SUBSCRIBE
          : SUCCESS_MESSAGES.CREDIT_MONITORING_UNSUBSCRIBE;
        SuccessToast(toastMessage);

        KissMetrics.trackEvent('Account - Updated CreditMonitoring Softpull Consent', {
          IsConsentingToCreditMonitoring: creditMonitoringConsent,
          WasSuccessful: true
        });
      })
      .catch((error) => {
        ErrorToast(ERROR_MESSAGES.UPDATE_CREDIT_MONITORING);
        dispatch(actions.updateCreditMonitoringConsentSuccess(!creditMonitoringConsent));
        dispatch(actions.updateCreditMonitoringConsentFailure(error));

        KissMetrics.trackEvent('Account - Updated CreditMonitoring Softpull Consent', {
          IsConsentingToCreditMonitoring: creditMonitoringConsent,
          WasSuccessful: false
        });

        console.error(error);
      });
  };
